import React from 'react';
import BaseDrawer from './BaseDrawer';
import { useAdminData } from '../../contexts/AdminDataContext';
import { CheckCircle, XCircle, Clock } from 'lucide-react';
interface ClaimDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  claimId: string | null;
}
const ClaimDetailDrawer: React.FC<ClaimDetailDrawerProps> = ({
  isOpen,
  onClose,
  claimId
}) => {
  const {
    claims,
    updateClaimStatus
  } = useAdminData();
  const claim = claims.find(c => c.id === claimId);
  const handleApprove = () => {
    if (claimId) {
      updateClaimStatus(claimId, 'Utilizzato');
      onClose();
    }
  };
  const handleReject = () => {
    if (claimId) {
      updateClaimStatus(claimId, 'Annullato');
      onClose();
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Utilizzato':
        return 'bg-green-100 text-green-800';
      case 'Riscattato':
        return 'bg-blue-100 text-blue-800';
      case 'Annullato':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  if (!claim) return null;
  return <BaseDrawer isOpen={isOpen} onClose={onClose} title="Dettagli Riscatto" description={`ID: ${claim.id}`} footer={<div className="space-y-3" data-id="element-586">
          {claim.status === 'Riscattato' && <div className="flex gap-3" data-id="element-587">
              <button onClick={handleReject} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors" data-id="element-588">
                <XCircle className="h-4 w-4" data-id="element-589" />
                Annulla Riscatto
              </button>
              <button onClick={handleApprove} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors" data-id="element-590">
                <CheckCircle className="h-4 w-4" data-id="element-591" />
                Marca come Utilizzato
              </button>
            </div>}
          <button onClick={onClose} className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-592">
            Chiudi
          </button>
        </div>} data-id="element-585">
      {/* Status Badge */}
      <div data-id="element-593">
        <span className="text-sm text-gray-500" data-id="element-594">Stato</span>
        <div className="mt-1" data-id="element-595">
          <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(claim.status)}`} data-id="element-596">
            {claim.status}
          </span>
        </div>
      </div>

      {/* Claim ID */}
      <div data-id="element-597">
        <span className="text-sm text-gray-500" data-id="element-598">ID Riscatto</span>
        <p className="font-mono text-lg font-semibold mt-1" data-id="element-599">{claim.id}</p>
      </div>

      {/* Premio Info */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-600">
        <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-601">Premio</h3>
        <p className="font-medium text-gray-900 mb-2" data-id="element-602">{claim.reward}</p>
        <div className="space-y-1 text-sm" data-id="element-603">
          <div className="flex items-center justify-between" data-id="element-604">
            <span className="text-gray-500" data-id="element-605">Partner:</span>
            <span className="font-medium" data-id="element-606">{claim.partner}</span>
          </div>
          <div className="flex items-center justify-between" data-id="element-607">
            <span className="text-gray-500" data-id="element-608">Costo:</span>
            <span className="font-medium" data-id="element-609">{claim.diamonds} crediti</span>
          </div>
          <div className="flex items-center justify-between" data-id="element-610">
            <span className="text-gray-500" data-id="element-611">Metodo:</span>
            <span className="font-medium" data-id="element-612">{claim.method}</span>
          </div>
          {claim.code !== '-' && <div className="flex items-center justify-between" data-id="element-613">
              <span className="text-gray-500" data-id="element-614">Codice:</span>
              <span className="font-mono font-medium text-xs" data-id="element-615">
                {claim.code}
              </span>
            </div>}
        </div>
      </div>

      {/* User Info */}
      <div data-id="element-616">
        <span className="text-sm text-gray-500" data-id="element-617">Utente (Pseudonimo)</span>
        <p className="font-mono text-base font-medium mt-1" data-id="element-618">{claim.userId}</p>
      </div>

      {/* Date and Location */}
      <div className="grid grid-cols-2 gap-4" data-id="element-619">
        <div data-id="element-620">
          <span className="text-sm text-gray-500" data-id="element-621">Data/Ora</span>
          <p className="text-sm font-medium text-gray-900 mt-1" data-id="element-622">{claim.date}</p>
        </div>
        <div data-id="element-623">
          <span className="text-sm text-gray-500" data-id="element-624">Luogo</span>
          <p className="text-sm font-medium text-gray-900 mt-1" data-id="element-625">
            {claim.location}
          </p>
        </div>
      </div>

      {/* Notes */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200" data-id="element-626">
        <h3 className="text-sm font-medium text-blue-900 mb-2" data-id="element-627">Note</h3>
        <p className="text-sm text-blue-800" data-id="element-628">{claim.notes}</p>
      </div>

      {/* Status Timeline */}
      {claim.status === 'Riscattato' && <div className="bg-amber-50 rounded-lg p-4 border border-amber-200" data-id="element-629">
          <div className="flex items-start gap-3" data-id="element-630">
            <Clock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-631" />
            <div data-id="element-632">
              <h4 className="font-semibold text-amber-900 mb-1 text-sm" data-id="element-633">
                In Attesa di Utilizzo
              </h4>
              <p className="text-sm text-amber-800" data-id="element-634">
                Questo riscatto è stato confermato ma non ancora utilizzato.
                Puoi marcarlo come utilizzato o annullarlo.
              </p>
            </div>
          </div>
        </div>}

      {claim.status === 'Utilizzato' && <div className="bg-green-50 rounded-lg p-4 border border-green-200" data-id="element-635">
          <div className="flex items-start gap-3" data-id="element-636">
            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-637" />
            <div data-id="element-638">
              <h4 className="font-semibold text-green-900 mb-1 text-sm" data-id="element-639">
                Riscatto Completato
              </h4>
              <p className="text-sm text-green-800" data-id="element-640">
                Questo riscatto è stato utilizzato con successo.
              </p>
            </div>
          </div>
        </div>}

      {claim.status === 'Annullato' && <div className="bg-red-50 rounded-lg p-4 border border-red-200" data-id="element-641">
          <div className="flex items-start gap-3" data-id="element-642">
            <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-643" />
            <div data-id="element-644">
              <h4 className="font-semibold text-red-900 mb-1 text-sm" data-id="element-645">
                Riscatto Annullato
              </h4>
              <p className="text-sm text-red-800" data-id="element-646">
                Questo riscatto è stato annullato.
              </p>
            </div>
          </div>
        </div>}
    </BaseDrawer>;
};
export default ClaimDetailDrawer;